import React from 'react';
import { Box, Skeleton, Typography, alpha, useTheme } from '@mui/material';
import TierBadge from './TierBadge';
import RatingDelta from './RatingDelta';
import StreakFlame from './StreakFlame';
import { tierColor } from './tierColors';
import { RankedRosterEntry } from '../../types/ranked';

export interface RankedSummaryStripProps {
  /** null when the student has no roster entry at all. */
  entry: RankedRosterEntry | null;
  /** Rated students in the season, for the "of N" half of the rank. */
  totalRanked: number;
  loading?: boolean;
  /** Show "3rd of 42". Default true. */
  showRank?: boolean;
}

/**
 * One line of ranked standing, for a parent's child card or a teacher's roster
 * card.
 *
 * The rank is shown as a bare "3rd of 42": it gives an adult the relative
 * position they will ask for first without naming any other family's child.
 * Widening this to a named leaderboard is a deliberate non-goal.
 */
const RankedSummaryStrip: React.FC<RankedSummaryStripProps> = ({
  entry,
  totalRanked,
  loading = false,
  showRank = true
}) => {
  const theme = useTheme();

  if (loading) {
    return <Skeleton variant="rounded" height={44} sx={{ borderRadius: 2 }} />;
  }

  // An unrated student has an entry (findMany defaults them to 1200/Bronze)
  // but no rank, so "has an entry" and "has a ranking" are different questions
  // and both land here.
  const rank = entry?.rank ?? null;

  if (!entry || rank == null) {
    return (
      <Box
        sx={{
          px: 1.5,
          py: 1,
          borderRadius: 2,
          border: `1px dashed ${theme.palette.divider}`,
          color: 'text.secondary'
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          Not ranked yet
        </Typography>
        <Typography variant="caption">
          Needs a live quiz with 3 or more mathletes
        </Typography>
      </Box>
    );
  }

  const color = tierColor(entry.tier.name);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.5,
        px: 1.5,
        py: 1,
        borderRadius: 2,
        bgcolor: alpha(color, 0.06),
        border: `1px solid ${alpha(color, 0.25)}`
      }}
    >
      <TierBadge tier={entry.tier.name} label={entry.tier.label} size="small" />

      <Typography
        sx={{
          fontFamily: 'monospace',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: 'text.secondary'
        }}
      >
        {entry.rating} SR
      </Typography>

      {entry.lastEvent && <RatingDelta delta={entry.lastEvent.delta} />}

      {showRank && (
        <Typography variant="body2" color="text.secondary">
          {ordinal(rank)} of {totalRanked}
        </Typography>
      )}

      <Box sx={{ ml: 'auto' }}>
        <StreakFlame streak={entry.streak} />
      </Box>
    </Box>
  );
};

function ordinal(n: number): string {
  const suffix = n === 1 ? 'st' : n === 2 ? 'nd' : n === 3 ? 'rd' : 'th';
  return `${n}${suffix}`;
}

export default RankedSummaryStrip;
